import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from "recharts";
import { getTokenomics } from "@/lib/api";

export default function Tokenomics() {
  const [data, setData] = useState({ items: [], total_supply: 0 });

  useEffect(() => {
    getTokenomics().then(setData).catch(() => {});
  }, []);

  const total = data.total_supply || 1_000_000_000;

  return (
    <section id="tokenomics" className="relative py-24 sm:py-32 bg-[#070708]" data-testid="tokenomics-section">
      <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-[#00F0FF]/40 to-transparent" />
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
        <div className="lg:col-span-6">
          <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#00F0FF]">// supply matrix</p>
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-black mt-2">Tokenomics</h2>
          <p className="mt-5 text-white/65 font-mono text-sm sm:text-base">
            Fixed supply of <span className="text-[#39FF14] font-bold">{(total / 1_000_000).toFixed(0)} million ARC</span> on
            BNB Smart Chain. No mint, no rebase, no auto-tax. All six allocations minted once in the
            constructor and locked forever.
          </p>

          <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-3">
            {data.items.map((it, i) => (
              <motion.div
                key={it.label}
                initial={{ opacity: 0, x: -10 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.05 }}
                className="flex items-center justify-between glass px-4 py-3"
                data-testid={`tokenomics-row-${it.label.toLowerCase().replace(/\s/g, "-")}`}
              >
                <div className="flex items-center gap-3">
                  <span className="w-3 h-3" style={{ background: it.color, boxShadow: `0 0 10px ${it.color}` }} />
                  <span className="font-mono text-xs uppercase tracking-wider">{it.label}</span>
                </div>
                <div className="text-right">
                  <div className="font-display font-bold" style={{ color: it.color }}>{it.value}%</div>
                  <div className="font-mono text-[10px] text-white/40">
                    {((total * it.value) / 100 / 1_000_000).toFixed(0)}M ARC
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>

        <div className="lg:col-span-6">
          <div className="relative h-[420px] glass p-4">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={data.items}
                  dataKey="value"
                  nameKey="label"
                  innerRadius={92}
                  outerRadius={150}
                  paddingAngle={3}
                  startAngle={90}
                  endAngle={-270}
                >
                  {data.items.map((it, i) => (
                    <Cell key={i} fill={it.color} stroke="#050505" strokeWidth={2} />
                  ))}
                </Pie>
                <Tooltip
                  contentStyle={{
                    background: "#0a0b10",
                    border: "1px solid rgba(0,240,255,0.3)",
                    borderRadius: "0",
                    fontFamily: "JetBrains Mono",
                    fontSize: "12px",
                  }}
                  formatter={(v, n) => [`${v}%`, n]}
                />
              </PieChart>
            </ResponsiveContainer>
            <div className="absolute inset-0 grid place-items-center pointer-events-none">
              <div className="text-center">
                <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/50">Total Supply</div>
                <div className="font-display text-3xl font-black shine-text">100M ARC</div>
                <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/40 mt-1">Fixed · Non-mintable</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
